<template>
  <div style="display: contents;">
  <v-dialog v-model="dialogOpen"
    v-if="dialogOpen"
    persistent
    max-width="630"
    content-class="account-details-dialog"
    @keydown.esc="backwardStep">
    <v-card class="glass-card-account-details">
      <v-form ref="accountForm"
        @submit.prevent="finishPayment">
        <v-card-title class="glass-header-account-details pa-0">
          <v-row class="ma-0 pa-4 align-center">
            <v-col cols="auto" class="pa-0">
              <v-icon color="white" size="28">mdi-account-card-details</v-icon>
            </v-col>
            <v-col class="pa-0 pl-3">
              <h3 class="white--text" style="font-weight: 600; font-size: 18px; letter-spacing: 0.3px;">
                {{ $t("$vuetify.accountDetailsTitle") }}
              </h3>
            </v-col>
            <v-spacer></v-spacer>
            <v-col cols="auto" class="pa-0 d-flex align-center">
              <span style="color: white; font-size: 13px; font-weight: 500; letter-spacing: 0.3px;">
                {{ $t("$vuetify.formDetails.saveAssociate") }}
              </span>
              <v-checkbox
                class="ml-2 mt-0 pt-0"
                v-model="doSaveAccount"
                dark
                hide-details
                dense
              ></v-checkbox>
            </v-col>
            <v-col cols="auto" class="pa-0 pl-3">
              <v-icon large color="white" @click="backwardStep">mdi-close</v-icon>
            </v-col>
          </v-row>
        </v-card-title>

        <v-card-text class="pt-4 px-4 pb-2">
          <v-container class="pa-0">
            <v-row class="input-row mb-3">
              <v-col cols="12" class="py-0">
                <div class="field-label">{{ $t('$vuetify.formDetails.oib') }}</div>
                <v-combobox clearable
                  @focus="showKbd"
                  data-layout="number"
                  :placeholder="$t('$vuetify.filterPlaceholderLabel')"
                  v-model="payerOib"
                  :search-input.sync="oibSearchInput"
                  :items="oibOptions"
                  :loading="loadingOib"
                  :rules="oibRules"
                  validate-on-blur
                  @update:search-input="onOibSearch"
                  @input="onOibInput"
                  @change="onOibChange"
                  @click:clear="clearForm"
                  @keydown.enter="finishPayment"
                  class="account-input uppercased-input"
                  outlined
                  dense
                  hide-details="auto"
                  item-text="oib"
                  item-value="oib"
                  return-object
                  maxlength="11"
                >
                  <template v-slot:item="{ item, on, attrs }">
                    <v-list-item v-on="on" v-bind="attrs" @click="selectAssociateByOib(item)">
                      <v-list-item-content>
                        <v-list-item-title>{{ item.oib }}</v-list-item-title>
                        <v-list-item-subtitle>{{ item.name }}</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </template>
                </v-combobox>
              </v-col>
            </v-row>

            <v-row class="input-row mb-3">
              <v-col cols="12" class="py-0">
                <div class="field-label">{{ $t('$vuetify.formDetails.name') }}</div>
                <v-combobox @focus="showKbd"
                  data-layout="normalAduro"
                  :placeholder="$t('$vuetify.filterPlaceholderLabel')"
                  v-model="payer"
                  :loading="loading"
                  :items="associates"
                  return-object
                  item-text="name"
                  item-value="name"
                  @change="selectAssociate(payer)"
                  :search-input.sync="payerName"
                  @update:search-input="querySelections"
                  hide-no-data
                  clearable
                  @click:clear="clearForm"
                  :rules="[rules.req]"
                  @keydown.enter="finishPayment"
                  class="account-input uppercased-input"
                  outlined
                  dense
                  hide-details="auto"
                >
                  <template v-slot:item="{ item, on, attrs }">
                    <v-list-item class="uppercased-input"
                      @keydown.enter.stop=""
                      v-on="on"
                      v-bind="attrs">{{ item.name }}</v-list-item>
                  </template>
                </v-combobox>
              </v-col>
            </v-row>

            <v-row class="input-row mb-3">
              <v-col cols="12" class="py-0">
                <div class="field-label">{{ $t('$vuetify.formDetails.address') }}</div>
                <v-text-field @focus="showKbd"
                  data-layout="normalAduro"
                  v-model="payerAddress"
                  :rules="payer && payer.name === 'Kupac građanin' ? [] : [rules.req]"
                  class="account-input uppercased-input"
                  outlined
                  dense
                  hide-details="auto"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-row class="input-row mb-3">
              <v-col cols="12" class="py-0">
                <div class="field-label">{{ $t('$vuetify.formDetails.city') }}</div>
                <v-text-field @focus="showKbd"
                  data-layout="normalAduro"
                  v-model="payerCity"
                  :rules="payer && payer.name === 'Kupac građanin' ? [] : [rules.req]"
                  class="account-input uppercased-input"
                  outlined
                  dense
                  hide-details="auto"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-row class="input-row mb-3">
              <v-col cols="12" class="py-0">
                <div class="field-label">{{ $t('$vuetify.formDetails.zipCode') }}</div>
                <v-text-field @focus="showKbd"
                  data-layout="normalAduro"
                  v-model="payerZip"
                  :rules="payer && payer.name === 'Kupac građanin' ? [] : [rules.req]"
                  class="account-input uppercased-input"
                  outlined
                  dense
                  hide-details="auto"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-row class="input-row mb-3">
              <v-col cols="12" class="py-0">
                <div class="field-label">{{ $t('$vuetify.formDetails.country') }}</div>
                <v-select @focus="showKbd"
                  data-layout="normalAduro"
                  :items="countries"
                  v-model="payerCountry"
                  item-text="country"
                  item-value="country"
                  :rules="payer && payer.name === 'Kupac građanin' ? [] : [rules.req]"
                  class="account-input uppercased-input"
                  outlined
                  dense
                  hide-details="auto"
                ></v-select>
              </v-col>
            </v-row>
            <v-row class="input-row pt-1 mt-0">
              <v-col cols="6"
                class="pr-1">

                <v-select :items="costCenters"
                  label="Odaberite mjesto troška"
                  v-model="selectedCostCenter"
                  outlined
                  dense
                  item-text="name"
                  :class="['uppercased-input', 'textField']"
                  hide-details
                  return-object>
                  <template v-slot:item="{ item }">
                    <span>{{ item.name }} {{ item.description ? ' - ' + item.description : '' }}
                      {{ item.address && item.address.street ? ', ' + item.address.street : "" }}
                      {{ item.address && item.address.city ? item.address.city : "" }}
                    </span>
                  </template>
                </v-select>
              </v-col>

              <v-col cols="2"
                class="pr-0">
                <v-btn @click="openCostCenterDialog()"
                  style="border-radius: 12px !important"
                  class="btn successButton textfield"><v-icon>mdi-plus</v-icon></v-btn>
              </v-col>
            </v-row>
            <v-row v-if="paymentMethod !== 'G' && paymentMethod !== 'K'"
              class="input-row pt-1 mt-0">
              <v-col>
                <v-select :items="pnbTypes"
                  v-model="pnbType"
                  class="textField"
                  item-text="text"
                  item-value="value"
                  persistent-placeholder
                  outlined
                  dense
                  @change="onPnbTypeChange">
                </v-select>
              </v-col>
            </v-row>
            <v-row v-if="paymentMethod !== 'G' && paymentMethod !== 'K'"
              class="input-row pt-1 mt-0">
              <v-col cols="3"
                class="pr-1">
                <v-text-field v-model="pnbModel"
                  prefix="HR"
                  label="Model"
                  class="textField"
                  persistent-placeholder
                  outlined
                  dense></v-text-field>
              </v-col>
              <v-col cols="9"
                class="pl-1">
                <v-text-field v-model="pnb"
                  label="Poziv na broj"
                  class="textField"
                  persistent-placeholder
                  outlined
                  dense></v-text-field>
              </v-col>
            </v-row>
            <v-row v-if="
              inEu === true &&
              payerCountry !== 'Hrvatska' &&
              payerCountry !== undefined
            "
              class="pt-0 mt-0">
              <v-col cols="12"
                class="d-flex pt-0 mt-0 py-0 justify-end">
                <v-btn class="mt-10 mr-10"
                  href="https://ec.europa.eu/taxation_customs/vies/?locale=hr"
                  target="_blank"
                  height="25">
                  Provjera VIES baze
                </v-btn>
                <v-radio-group v-model="selectedVies"
                  :rules="[(v) => !!v || 'Molimo odaberite']"
                  required
                  class="small-radio">
                  <v-radio v-for="vies in vieses"
                    :key="vies.value"
                    :value="vies.value"
                    :label="vies.name"
                    :rules="[rules.req]"></v-radio>
                </v-radio-group>
              </v-col>
            </v-row>
            <v-row v-if="
              inEu === false &&
              payerCountry !== 'Hrvatska' &&
              payerCountry !== undefined
            "
              class="pt-0 mt-0">
              <v-col cols="12"
                class="d-flex pt-0 mt-0 py-0 justify-end">
                <v-radio-group v-model="selectedCustomerType"
                  :rules="[(v) => !!v || 'Molimo odaberite']"
                  required
                  class="small-radio">
                  <v-radio v-for="type in customerTypes"
                    :key="type.value"
                    :value="type.value"
                    :label="type.name"
                    :rules="[rules.req]">
                  </v-radio>
                </v-radio-group>
              </v-col>
            </v-row>

          </v-container>
        </v-card-text>

        <v-card-actions class="d-flex justify-end px-4 pb-4">
          <v-btn
            outlined
            color="primary"
            @click="backwardStep"
            class="cancel-btn-account-details"
            elevation="0"
          >
            Nazad
          </v-btn>
          <v-btn
            color="primary"
            :loading="submitting"
            type="submit"
            class="submit-btn-account-details ml-3"
            elevation="0"
          >
            {{ $t("$vuetify.finishPayment") }}
          </v-btn>
        </v-card-actions>
      </v-form>
    </v-card>

    <v-container class="kbd"
      v-if="!$vuetify.breakpoint.smAndDown"
      ref="keyboard"
      v-show="oskVisible"
      :style="{ 'max-width': width }">
      <div class="kbd-header"></div>
      <vue-touch-keyboard :options="oskOptions"
        :layout="layout"
        :cancel="hideKbd"
        :accept="accept"
        :input="input" />
      <cost-center-dialog @addCostCenter="addCostCenter"
        ref="costCenterDialog"></cost-center-dialog>
    </v-container>
  </v-dialog>
  </div>
</template>

<script>
import state from '@/state'
import rules from '@/plugins/rules'
import countries from '@/mixins/countries'
import { clone, toISOLocal } from '@/plugins/utils'
import { df } from '@/plugins/firebase'
import { v4 as uuidv4 } from 'uuid'
import { printerInit } from '@/libs/bixolon/printer'
import onScreenKeyboard from '@/mixins/onScreenKeyboard'
import { callAssociates, callBusinessEntities } from '@/api/associates'
import CostCenterDialog from '@/modules/company/components/CostCenterDialog.vue'

export default {
  components: {
    CostCenterDialog
  },
  mixins: [countries, onScreenKeyboard],
  inject: ['showMsgBox', 'showLoader', 'hideLoader', 'openPdfPrint'],
  data () {
    return {
      templatesDialogOpen: false,
      dialogOpen: false,
      loading: false,
      listeners: [],
      defaultAssociates: [],
      associates: [],
      fiscalize: false,
      payerName: undefined,
      payer: undefined,
      payerAddress: undefined,
      payerCity: undefined,
      payerZip: undefined,
      payerCountry: undefined,
      payerOib: undefined,
      rules: {
        req: rules.req(),
        oib: rules.oib()
      },
      inEu: false,
      languages: [],
      language: {},
      note: undefined,
      receiptNumber: 1,
      offer_sequence: undefined,
      bill_sequence: undefined,
      last_receipt: undefined,
      minIssueDate: undefined,
      flag: undefined,
      doSaveAccount: false,
      saveAssociateEnabled: true,
      submitting: false,
      euCountries: [],
      takenOibs: [],
      selectedVies: undefined,
      selectedCustomerType: undefined,
      wholeSaleRcptTypes: undefined,
      costCenters: [],
      selectedCostCenter: undefined,
      pnb: undefined,
      pnbModel: undefined,
      pnbType: 0,
      pnbTypes: [{ text: 'Generiraj poziv po broju računa', value: 0, disabled: false }, { text: 'Generiraj poziv po OIB-u', value: 1, disabled: true }],
      // Trigram search variables
      oibSearchInput: '',
      oibOptions: [],
      loadingOib: false,
      oibSearchTimer: null,
      nameSearchTimer: null,
      isSelectingOib: false,
      isSelectingName: false,
      lastOibSearchHadResults: false,
      lastOibQuery: ''
    }
  },

  props: {
    data: {
      type: Object,
      default: () => { }
    },
    r1DialogOpen: {
      type: Boolean,
      default: false
    },
    paymentMethod: {
      type: String,
      default: ''
    },
    // selectedTable: {
    //   type: Object,
    //   default: () => {}
    // },
    // selectedTables: {
    //   type: Array,
    //   default: () => []
    // },
    selectedCard: {
      type: Object,
      default: () => { }
    },
    selectedPrinter: {
      type: Object,
      default: () => { }
    },
    selectedTerminal: {
      type: Object,
      default: () => { }
    },
    showPreview: {
      type: Boolean,
      default: false
    }
  },

  watch: {
    payerCountry (nv) {
      this.inEu = false
      this.euCountries.forEach(country => {
        if (country === nv) {
          this.inEu = true
        }
      })
    },
    storeReceiptNumber (nv) {
      this.receiptNumber = nv + 1
      this.calculateReferenceNumber()
    },
    payerOib (nv) {
      if (nv && nv.length === 11) {
        if (this.takenOibs.includes(nv)) {
          this.doSaveAccount = false
          this.saveAssociateEnabled = false
        } else {
          this.saveAssociateEnabled = true
        }
      }
    }
  },

  computed: {
    oibRules () {
      if (this.payerCountry !== 'Hrvatska') return []
      const extractOib = v => (v && typeof v === 'object' ? v.oib : v)
      return [
        v => this.rules.req(extractOib(v)),
        v => this.rules.oib(extractOib(v))
      ]
    },
    receiptNum () {
      if (!state.getPointOfSale()) { return 'Broj računa' }
      if (!state.getCashRegister()) { return 'Broj računa' }

      return this.receiptNumber +
        state.getCurrentCompany().receipt_numeration_separator +
        state.getPointOfSale().location_id +
        state.getCurrentCompany().receipt_numeration_separator +
        state.getCashRegister().number
    },
    vieses () {
      return [
        { name: 'Kupac unutar VIES baze', value: 'inside' },
        { name: 'Kupac izvan VIES baze', value: 'outside' }
      ]
    },
    customerTypes () {
      return [
        { name: 'Kupac - pravna osoba', value: 'legal' },
        { name: 'Kupac - fizička osoba', value: 'person' }
      ]
    },
    storeReceiptNumber () {
      if (this.receiptType && this.receiptType.value === 'P') {
        return this.offer_sequence
      } else {
        return this.bill_sequence
      }
    }
  },

  methods: {
    onPnbTypeChange () {
      this.calculateReferenceNumber()
    },
    calculateReferenceNumber () {
      let model = '00'
      let receiptNum = clone(this.receiptNum)
      receiptNum = receiptNum.replaceAll('/', '-')
      if (this.pnbType === 0) {
        let formattedReceiptNumber = receiptNum.replace(/[^0-9-]+/, '')
        formattedReceiptNumber = formattedReceiptNumber.replaceAll('--', '-')
        this.pnbModel = model
        this.pnb = formattedReceiptNumber
      } else if (this.pnbType === 1) {
        const oib = this.getOib()
        if (!oib) return
        model = '01'
        const randomKey = Math.floor(Math.random() * (99999999 - 10000000 + 1)) + 10000000
        const forSecurity = oib.toString() + randomKey.toString()
        const securityNum = this.controlDigit(forSecurity)
        this.pnbModel = model
        this.pnb = oib + '-' + randomKey + '-' + securityNum
      }
    },
    getOib () {
      let associate

      if (this.payer) {
        associate = this.payer
      }
      if (this.payerOib && typeof (this.payerOib) !== 'string') {
        associate = this.payerOib
      }

      if (typeof (associate) === 'string') {
        // slobodan unos
        return this.payerOib
      } else {
        // Odabir
        if (associate) {
          return associate.oibs[0]
        }
      }
    },
    controlDigit (s) {
      var i = 0; var v = 0; var p = 2; var c = ' '
      for (i = s.length; i >= 1; i--) {
        c = s.substr(i - 1, 1)
        if (c >= '0' && c <= '9' && v >= 0) { v = v + p * c; p = p + 1 } else { v = -1 }
      };
      if (v >= 0) { v = 11 - (v % 11); if (v > 9) { v = 0 }; }
      return (v)
    },
    addCostCenter (costCenter) {
      var cc = clone(costCenter)
      cc.address.zip_code = cc.address.zipCode
      this.costCenters.push(cc)
    },
    openCostCenterDialog () {
      this.$refs.costCenterDialog.open(false)
    },
    selectAssociate (payer) {
      this.setForm(true)
      this.setCostCenters(payer)
      this.calculateReferenceNumber()
      this.pnbTypes[1].disabled = false
    },
    setCostCenters (associate) {
      this.costCenters = []
      if (associate && associate.cost_centers) {
        this.costCenters = Object.values(associate.cost_centers)
      }
      this.costCenters.push({ id: -1, name: 'Osnovno mjesto troška' })
    },
    setDefaults () {
      this.fiscalize = true
      this.languages = [
        { name: this.$t('$vuetify.receiptLanguages.hr'), value: 'hr' },
        { name: this.$t('$vuetify.receiptLanguages.en'), value: 'en' }
      ]
      this.language = this.languages[0]
      this.note = ''
      this.flag = 'R'
    },

    setForm (data = true) {
      if (data) {
        this.referenceNumber = ''
        if (this.payer && typeof this.payer === 'object') {
          // this.payerCountry = this.payer.country
          this.payerCountry = 'Hrvatska'
          this.payerOib = this.payer.oibs ? this.payer.oibs[0] : ''
          this.payerAddress = this.payer.address
          this.payerCity = this.payer.city
          this.payerZip = this.payer.zip_code

          this.saveAssociateEnabled = false
        }
      } else {
        this.referenceNumber = ''
        this.associates = clone(this.defaultAssociates)
        this.saveAssociateEnabled = true
        this.$nextTick(() => {
          // this.$refs.accountForm.reset()
        })
      }
    },

    clearForm () {
      // Clear all form fields
      this.payer = undefined
      this.payerName = undefined
      this.payerOib = undefined
      this.payerAddress = undefined
      this.payerCity = undefined
      this.payerZip = undefined
      this.payerCountry = 'Hrvatska'
      this.associates = []
      this.oibOptions = []
      this.oibSearchInput = ''
      this.doSaveAccount = false
      this.saveAssociateEnabled = true
      this.selectedCostCenter = undefined
      this.costCenters = [{ id: -1, name: 'Osnovno mjesto troška' }]
      this.pnbTypes[1].disabled = true
      // Reset search state
      this.lastOibSearchHadResults = false
      this.lastOibQuery = ''
      this.loadingOib = false
      this.isSelectingOib = false
      this.isSelectingName = false
      this.calculateReferenceNumber()
    },

    getMultiplier () {
      const listener = df.doc('project_constants/config').onSnapshot(doc => {
        this.config = doc.data()
        this.paymentModels = doc.data().payment_models
        this.multiplier = doc.data().item_amount.decimal_multiplier
      })
      this.listeners.push(listener)
    },

    getEuCountries () {
      const listener = df.doc('project_constants/countries').onSnapshot(doc => {
        this.euCountries = doc.data().eu_countries
      })
      this.listeners.push(listener)
    },

    querySelections () {
      // Skip search if user is selecting from dropdown
      if (this.isSelectingName) {
        this.isSelectingName = false
        return
      }

      // Debounce search - wait 300ms after user stops typing
      clearTimeout(this.nameSearchTimer)

      if (!this.payerName || this.payerName.length < 3) {
        this.associates = []
        return
      }

      this.nameSearchTimer = setTimeout(async () => {
        this.loading = true
        await this.searchAssociatesByName(this.payerName)
        this.loading = false
      }, 300)
    },

    async searchAssociatesByName (nameQuery) {
      if (!nameQuery || nameQuery.length < 3) {
        this.associates = []
        return
      }

      try {
        const path = `/associates/${state.getCurrentCompany().id}/associate?filter=trigram(eq:${encodeURIComponent(nameQuery)})`
        const response = await callAssociates('get', path)

        if (response && response.status === 200 && response.data && response.data.associates) {
          this.associates = Object.keys(response.data.associates)
            .map((key) => response.data.associates[key])
            .filter((associate) => associate.status === 'OK')
            .sort((a, b) => a.name.localeCompare(b.name))
        } else {
          this.associates = []
        }
      } catch (error) {
        console.error('Error searching associates by name:', error)
        this.associates = []
      }
    },

    // Validacija OIB unosa - samo brojevi, max 11
    onOibInput (value) {
      if (typeof value === 'string') {
        // Ukloni sve što nije broj
        const numbersOnly = value.replace(/[^\d]/g, '')
        // Ograniči na 11 znamenaka
        const limited = numbersOnly.slice(0, 11)

        if (limited !== value) {
          this.$nextTick(() => {
            this.payerOib = limited
            this.oibSearchInput = limited
          })
        }
      }
    },

    onOibSearch (searchValue) {
      // Skip search if user is selecting from dropdown
      if (this.isSelectingOib) {
        this.isSelectingOib = false
        return
      }

      // Debounce search - wait 300ms after user stops typing
      clearTimeout(this.oibSearchTimer)

      if (!searchValue || searchValue.length < 3) {
        this.oibOptions = []
        this.lastOibSearchHadResults = false
        this.lastOibQuery = ''
        return
      }

      // Ako je tačno 11 znamenaka, uvijek traži (businessentities check)
      if (searchValue.length === 11) {
        this.oibSearchTimer = setTimeout(async () => {
          await this.searchAssociatesByOib(searchValue)
        }, 300)
        return
      }

      // Ako je prošla pretraga bila neuspješna i novi query počinje sa istim karakterima, ne traži
      if (!this.lastOibSearchHadResults &&
          this.lastOibQuery &&
          searchValue.startsWith(this.lastOibQuery)) {
        return
      }

      this.oibSearchTimer = setTimeout(async () => {
        await this.searchAssociatesByOib(searchValue)
      }, 300)
    },

    async searchAssociatesByOib (oibQuery) {
      if (!oibQuery || oibQuery.length < 3) {
        this.oibOptions = []
        this.lastOibSearchHadResults = false
        this.lastOibQuery = ''
        return
      }

      try {
        this.loadingOib = true

        // Resetuj oibOptions prije pretrage
        this.oibOptions = []

        // Trigram pretraga u associates
        const path = `/associates/${state.getCurrentCompany().id}/associate?filter=trigram(eq:${encodeURIComponent(oibQuery)})`
        const response = await callAssociates('get', path)

        if (response && response.status === 200 && response.data && response.data.associates) {
          const associatesList = Object.keys(response.data.associates)
            .map((key) => response.data.associates[key])
            .filter((associate) => associate.status === 'OK')

          // Za OIB search, flatten all OIBs and create options
          const oibOptionsArray = []
          associatesList.forEach(associate => {
            if (associate.oibs && associate.oibs.length > 0) {
              associate.oibs.forEach(oib => {
                if (oib.toLowerCase().includes(oibQuery.toLowerCase())) {
                  oibOptionsArray.push({
                    oib: oib,
                    name: associate.name,
                    address: associate.address,
                    city: associate.city,
                    zip_code: associate.zip_code,
                    country: associate.country,
                    fullAssociate: associate
                  })
                }
              })
            }
          })

          this.oibOptions = oibOptionsArray

          // Zapamti da li je pretraga imala rezultate
          this.lastOibSearchHadResults = oibOptionsArray.length > 0
          this.lastOibQuery = oibQuery
        } else {
          this.lastOibSearchHadResults = false
          this.lastOibQuery = oibQuery
        }

        // Ako je TAČNO 11 znamenaka i nema rezultata iz associates, pokušaj businessentities
        if (oibQuery.length === 11 && this.oibOptions.length === 0) {
          await this.checkBusinessEntities(oibQuery)
          // Ako businessentities nađe nešto, ažuriraj flag
          if (this.oibOptions.length > 0) {
            this.lastOibSearchHadResults = true
          }
        }
      } catch (error) {
        console.error('Error searching associates by OIB:', error)
        this.oibOptions = []
        this.lastOibSearchHadResults = false
        this.lastOibQuery = oibQuery
      } finally {
        this.loadingOib = false
      }
    },

    async checkBusinessEntities (oib) {
      if (!oib || oib.length !== 11) {
        return
      }

      try {
        const response = await callBusinessEntities(oib)

        // Struktura je response.data.data.business_entity (axios dodaje jedan nivo)
        if (response && response.status === 200 && response.data && response.data.data && response.data.data.business_entity) {
          const entity = response.data.data.business_entity

          // Mapiranje iz businessentities strukture
          const name = entity.name || ''
          const address = entity.headquatersAddress || ''
          const city = entity.headquatersCity || ''
          const zip_code = entity.headquatersZip || ''

          // Dodaj u oibOptions ako već nije tu
          const exists = this.oibOptions.some(opt => opt.oib === oib)

          if (!exists) {
            this.oibOptions.unshift({
              oib: oib,
              name: name,
              address: address,
              city: city,
              zip_code: zip_code,
              country: 'Hrvatska',
              fullAssociate: {
                name: name,
                oibs: [oib],
                address: address,
                city: city,
                zip_code: zip_code,
                country: 'Hrvatska'
              }
            })
          }
        }
      } catch (error) {
        // OIB not found in business entities registry
      }
    },

    onOibChange (selectedValue) {
      // When user selects from dropdown (by click or enter)
      if (selectedValue && typeof selectedValue === 'object' && selectedValue.fullAssociate) {
        this.isSelectingOib = true
        this.selectAssociateByOib(selectedValue)
        this.oibOptions = []
      }
    },

    selectAssociateByOib (selectedItem) {
      if (selectedItem && selectedItem.fullAssociate) {
        const associate = selectedItem.fullAssociate

        this.isSelectingName = true

        this.payer = associate
        this.payerOib = selectedItem.oib
        this.payerAddress = associate.address || ''
        this.payerCity = associate.city || ''
        this.payerZip = associate.zip_code || ''
        this.payerCountry = associate.country || 'Hrvatska'
        this.saveAssociateEnabled = false

        // Set cost centers and enable PNB generation
        this.setCostCenters(associate)
        this.calculateReferenceNumber()
        this.pnbTypes[1].disabled = false
      }
    },

    backwardStep () {
      this.setForm(false)
      this.$emit('closeR1Dialog')
    },

    dateTimeToUnixTimestamp (dt) {
      return Math.floor(new Date(toISOLocal(dt)).getTime() / 1000)
    },

    async finishPayment () {
      if (!this.$refs.accountForm.validate()) return
      const type = {
        flag: this.flag,
        payer_name: this.payerName,
        payer_address: this.payerAddress,
        payer_city: this.payerCity,
        payer_zip_code: this.payerZip,
        payer_country: this.payerCountry,
        payer_oib: this.payerOib
      }

      if (this.doSaveAccount) {
        if (this.takenOibs.includes(type.payer_oib)) {
          console.log('oib vec postoji')
          this.doSaveAccount = false
          this.saveAssociateEnabled = false
          return
        }
        this.saveAssociate()
      }

      let wholeSaleRcptType = ''

      // POTREBNO ZBOG ISPISA ČLANKA NA RAČUNU
      if (this.payerCountry !== undefined && this.payerCountry !== 'Hrvatska') {
        if (state.getCurrentCompany().is_taxed === true) {
          if (this.inEu) {
            if (this.selectedVies === 'inside') {
              wholeSaleRcptType = 'TAX_EU_VIES_TRUE'
            } else {
              wholeSaleRcptType = 'TAX_EU_VIES_FALSE'
            }
          } else {
            if (this.selectedCustomerType === 'person') {
              wholeSaleRcptType = 'TAX_OTHER_PERSON'
            } else {
              wholeSaleRcptType = 'TAX_OTHER_LEGAL_ENTITY'
            }
          }
        } else {
          if (this.inEu) {
            if (this.selectedVies === 'inside') {
              wholeSaleRcptType = 'EU_VIES_TRUE'
            } else {
              wholeSaleRcptType = 'EU_VIES_FALSE'
            }
          } else {
            if (this.selectedCustomerType === 'person') {
              wholeSaleRcptType = 'OTHER_PERSON'
            } else {
              wholeSaleRcptType = 'OTHER_LEGAL_ENTITY'
            }
          }
        }
      }

      const data = {
        type: type,
        card: this.selectedCard,
        wholesale_receipt_type: wholeSaleRcptType
      }

      if (this.selectedCostCenter && this.selectedCostCenter.id !== -1) {
        data.selectedCostCenter = this.selectedCostCenter
      }

      if (this.pnb) {
        data.pnb = this.pnb
        data.pnbModel = 'HR' + this.pnbModel
      }

      if (this.showPreview === false) {
        this.$emit('accountDetailsFinishPayment', data)
      } else {
        this.$emit('accountDetailsPreviewReceipt', data)
      }

      this.setForm(false)
      this.$emit('closeR1Dialog')
    },

    async saveAssociate () {
      // Ekstraktuj samo OIB string (može biti objekat ili string)
      const oibValue = typeof this.payerOib === 'object' ? this.payerOib.oib : this.payerOib
      const oibs = [oibValue]

      // Ekstraktuj samo ime (može biti objekat ili string)
      const nameValue = typeof this.payer === 'object' ? this.payer.name : (this.payerName || this.payer)

      const payload = {
        action: {
          operation: 'set',
          entity: 'associate',
          params: {
            company_id: state.getCurrentCompany().id,
            name: nameValue.toLowerCase(),
            oibs: oibs,
            address: this.payerAddress.toLowerCase(),
            city: this.payerCity.toLowerCase(),
            zip_code: this.payerZip,
            country: this.payerCountry,
            associate_type: { BUYER: true }
          }
        }
      }

      if (this.costCenters && this.costCenters.length > 0) {
        payload.action.params.cost_centers = this.costCenters.reduce((acc, center) => {
          if (center.id !== -1) {
            var id = uuidv4()
            center.id = id
            acc[id] = center
          }
          return acc
        }, {})
      }

      var path = `/associates/${state.getCurrentCompany().id}/associate`
      var response = await callAssociates('post', path, payload.action.params)

      // Provjeri da li je HTTP status 200 I da li je response.data.status === 'Error'
      if (response.status === 200 && response.data && response.data.status !== 'Error') {
        console.info(`Klijent "${nameValue}" uspješno spremljen`)
      } else {
        // Dohvati error poruku iz različitih mogućih lokacija
        const errorMessage = response?.data?.message || response?.data?.error || ''

        // Provjeri da li se radi o dupliciranom klijentu
        if (errorMessage.includes('već postoji') || errorMessage.includes('already exists')) {
          console.warn('Klijent već postoji, neće biti ponovno spremljen')
        } else {
          this.showMsgBox({
            text: errorMessage || 'Neuspjelo dodavanje novog klijenta',
            actions: ['cancel'],
            cancelBtnText: 'OK',
            color: 'error'
          })
        }
        this.submitting = false
      }
    },

    detachListeners () {
      this.listeners.forEach((listener) => {
        listener()
        this.listeners = this.listeners.filter((l) => l !== listener)
      })
    },
    async getAssociates () {
      var path = `/associates/${state.getCurrentCompany().id}/associate`
      var response = await callAssociates('get', path)

      if (response && response.status === 200 && response.data && response.data.associates) {
        this.defaultAssociates = Object.keys(response.data.associates)
          .map((key) => {
            return response.data.associates[key]
          })
          .filter((associate) => associate.status === 'OK')

        this.defaultAssociates.push({ name: 'Kupac građanin', id: 'kupac-gradanin' })
        this.associates = clone(this.defaultAssociates.sort((a, b) => a.name.localeCompare(b.name)))
        // Get all used up OIBs
        this.associates.forEach(item => {
          if (item?.oibs && item.oibs.length > 0) {
            item.oibs.forEach(oib => {
              this.takenOibs.push(oib)
            })
          }
        })
      } else if (response.status === 404) {
        this.associates = []
        // Klijenti nisu pronađeni - tiho nastavi bez obavijesti
      } else {
        this.showMsgBox({
          text: 'Neuspjelo dohvaćanje klijenata',
          actions: ['cancel'],
          cancelBtnText: 'OK',
          color: 'error'
        })
      }
    }

  },

  created () {
    this.dialogOpen = true
  },

  mounted () {
    // document.getElementById('oibInput').click()
    if (state.getPrinter() === 'BIXOLON80') {
      const issueID = 1
      printerInit(issueID)
      state.setPosPrinterSettings(2)
    }

    this.setDefaults()
    this.getMultiplier()
    this.getEuCountries()

    this.payerCountry = 'Hrvatska'

    // this.getAssociates()

    this.minIssueDate = toISOLocal(new Date())
    this.calculateReferenceNumber()

    this.listeners.push(
      df
        .doc(`cash_registers/${state.getCashRegister().id}`)
        .onSnapshot((doc) => {
          if (doc && doc.data()) {
            this.offer_sequence = doc.data().offer_sequence
            this.bill_sequence = doc.data().bill_sequence
            this.last_receipt = doc.data().last_receipt_issued_timestamp
            if (this.last_receipt) {
              this.minIssueDate = toISOLocal(
                new Date(this.last_receipt * 1000)
              )
            } else {
              this.minIssueDate = toISOLocal(new Date())
            }
          }
        })
    )
  },

  beforeDestroy () {
    this.detachListeners()
  }
}
</script>

<style scoped>
/* Glass Card with Brand Gradient Header */
.glass-card-account-details {
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(20px) !important;
  border-radius: 16px !important;
  border: 1px solid rgba(0, 179, 190, 0.2) !important;
  box-shadow: 0 8px 32px rgba(0, 179, 190, 0.15) !important;
  overflow: hidden;
  max-height: 90vh;
  overflow-y: auto;
}

.glass-header-account-details {
  background: linear-gradient(135deg,
    rgba(0, 179, 190, 0.9) 0%,
    rgba(57, 137, 201, 0.85) 50%,
    rgba(97, 84, 164, 0.9) 100%) !important;
  position: sticky;
  top: 0;
  z-index: 1;
}

/* Field Labels */
.field-label {
  font-size: 12px;
  color: rgba(57, 137, 201, 0.8);
  font-weight: 600;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  margin-bottom: 6px;
}

/* Input Fields */
.account-input {
  border-radius: 10px !important;
}

.account-input >>> .v-input__control {
  border-radius: 10px !important;
}

.account-input >>> fieldset {
  border-color: rgba(57, 137, 201, 0.25) !important;
  border-width: 1.5px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.account-input >>> .v-input__slot {
  background: rgba(255, 255, 255, 0.8) !important;
  backdrop-filter: blur(10px) !important;
}

.account-input:hover >>> fieldset {
  border-color: rgba(57, 137, 201, 0.4) !important;
}

.account-input.v-input--is-focused >>> fieldset {
  border-color: rgba(57, 137, 201, 0.7) !important;
  border-width: 2px !important;
  box-shadow: 0 0 0 3px rgba(57, 137, 201, 0.1) !important;
}

.account-input.error--text >>> fieldset {
  border-color: rgba(244, 67, 54, 0.7) !important;
  background: rgba(244, 67, 54, 0.05) !important;
}

/* Input Row Spacing */
.input-row {
  margin-bottom: 12px;
}

/* Buttons */
.cancel-btn-account-details {
  background: rgba(255, 255, 255, 0.9) !important;
  color: #3989C9 !important;
  border: 1px solid rgba(57, 137, 201, 0.25) !important;
  border-radius: 12px !important;
  box-shadow: 0 2px 8px rgba(57, 137, 201, 0.12) !important;
  backdrop-filter: blur(10px) !important;
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  padding: 10px 24px !important;
  height: 40px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.cancel-btn-account-details:hover {
  background: rgba(57, 137, 201, 0.1) !important;
  border-color: rgba(57, 137, 201, 0.4) !important;
  box-shadow: 0 4px 12px rgba(57, 137, 201, 0.2) !important;
  transform: translateY(-2px);
}

.submit-btn-account-details {
  background: linear-gradient(135deg,
    rgba(0, 179, 190, 0.95) 0%,
    rgba(57, 137, 201, 0.9) 50%,
    rgba(97, 84, 164, 0.95) 100%) !important;
  color: white !important;
  border-radius: 12px !important;
  box-shadow: 0 4px 16px rgba(57, 137, 201, 0.3) !important;
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  padding: 10px 32px !important;
  height: 40px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.submit-btn-account-details:hover {
  background: linear-gradient(135deg,
    rgba(0, 179, 190, 1) 0%,
    rgba(57, 137, 201, 1) 50%,
    rgba(97, 84, 164, 1) 100%) !important;
  box-shadow: 0 6px 20px rgba(57, 137, 201, 0.4) !important;
  transform: translateY(-2px);
}

/* Uppercase Input Text */
.uppercased-input >>> input {
  text-transform: uppercase !important;
}

.uppercased-input >>> .v-select__selection {
  text-transform: uppercase !important;
}

/* Small Radio Buttons */
::v-deep .small-radio i {
  font-size: 19px;
}

::v-deep .small-radio label {
  font-size: 14px;
  padding-left: 0px;
  margin-left: -4px;
}

::v-deep .small-radio .v-radio {
  padding: 0px;
}

::v-deep .small-radio [class*="__ripple"] {
  left: 0;
}
</style>
